export type PollingBackoffError = {
  status?: number;
  response?: unknown;
  message?: string;
  name?: string;
};

type NextPollDelayInput = {
  error?: PollingBackoffError | null;
  attempt: number;
  lastStatus?: number | undefined;
  baseIntervalMs: number;
  capMs: number;
  remainingBudgetMs: number;
};

type NextPollDelayResult = {
  delayMs: number;
  shouldStop: boolean;
  attemptNext: number;
  lastStatusNext: number | undefined;
};

const STOPPING_STATUS_CODES = new Set([403, 429]);
const STOP_AFTER_ATTEMPTS = 3;

/**
 * Computes the next polling delay for transient artwork polling failures.
 *
 * The stop policy is "3 consecutive 403 OR 3 consecutive 429" — per status.
 * A run is broken when the status changes, so a 403 → 429 → 403 sequence
 * counts as one consecutive 403 (twice) and one consecutive 429, none of
 * which reach the stop threshold. Callers must pass and persist
 * `lastStatus` between invocations for the per-status run to be tracked
 * correctly.
 *
 * Examples:
 * - Success resets attempts and returns the base interval.
 * - The first 429 returns a 10s delay, attempt 1, lastStatus 429.
 * - A 429 followed by a second 429 returns a 20s delay, attempt 2.
 * - Three consecutive 429s (or 403s) asks the caller to stop.
 * - 403 → 429 → 403 resets the counter twice; no stop.
 */
export function nextPollDelay({
  error,
  attempt,
  lastStatus,
  baseIntervalMs,
  capMs,
  remainingBudgetMs
}: NextPollDelayInput): NextPollDelayResult {
  if (!error) {
    return {
      delayMs: baseIntervalMs,
      shouldStop: false,
      attemptNext: 0,
      lastStatusNext: undefined
    };
  }

  const { status } = error;

  if (status === 401) {
    return {
      delayMs: baseIntervalMs,
      shouldStop: true,
      attemptNext: 0,
      lastStatusNext: undefined
    };
  }

  if (!status || status >= 500 || !STOPPING_STATUS_CODES.has(status)) {
    return {
      delayMs: baseIntervalMs,
      shouldStop: false,
      attemptNext: 0,
      lastStatusNext: undefined
    };
  }

  // Per-status consecutive counting: reset attempt to 0 when the status
  // changes (e.g., 403 → 429), so the new status starts its own run.
  const currentAttempt = status === lastStatus ? attempt : 0;
  const delayMs = Math.min(baseIntervalMs * 2 ** currentAttempt, capMs);
  const attemptNext = currentAttempt + 1;
  const shouldStop = attemptNext >= STOP_AFTER_ATTEMPTS || delayMs > remainingBudgetMs;

  return { delayMs, shouldStop, attemptNext, lastStatusNext: status };
}
